import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../components/context/AuthContext';
import { useSocket } from '../components/context/SocketProvider';

export const useAbsenceNotifications = () => {
    const [pendingCount, setPendingCount] = useState(0);
    // ⭐ FIX: also destructure `loading`. AuthContext hydrates `user`
    // IMMEDIATELY from a cached localStorage profile on page load, before the
    // real __session cookie exists (the login round trip is still in
    // flight). Gating on `user` alone meant this hook fired against that
    // cached-but-not-yet-authenticated state and got a 403 from the backend
    // every time — harmless (it retries once the real session resolves and
    // `user` updates again), but noisy. `loading` only goes false once auth
    // has genuinely settled, so waiting for it avoids the wasted request.
    const { user, loading } = useAuth();
    const socket = useSocket();

    const fetchCount = useCallback(async () => {
        if (user?.role === 'admin' || user?.role === 'manager') {
            try {
                const { data } = await api.get('/absences/pending-count');
                setPendingCount(data.count);
            } catch (err) {
                console.error("Error fetching absence count", err);
            }
        }
    }, [user]);

    useEffect(() => {
        if (loading) return; // wait for the real session, not just the cached profile
        fetchCount();

        if (!socket) return;

        // ⭐ FIX: the backend emits 'absence-update' when a request is created,
        // approved, rejected, edited or deleted (absenceController). This hook
        // used to listen for 'absence_status_updated', which nothing ever
        // emits, so the badge never cleared after a request was dealt with
        // until the page was reloaded.
        const handleChange = () => fetchCount();

        socket.on('new_absence_request', handleChange);
        socket.on('absence-update', handleChange);

        return () => {
            // ⭐ FIX: pass the handler. socket.off(event) with no handler removes
            // EVERY listener for that event app-wide, which would also kill the
            // Staff Absence page's own 'absence-update' live refresh.
            socket.off('new_absence_request', handleChange);
            socket.off('absence-update', handleChange);
        };
    }, [loading, socket, fetchCount]);

    return { pendingCount, refreshCount: fetchCount };
};